import React, { useEffect, useState } from 'react';

import './Preview.scss';
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import SectionTitleBar from '../components/titlebar';
import { ToBillArguments, ToBillEvent } from '../../models/Factura';
import { numberToCurrencyString } from '../../services/formatting';

import { useMutation } from '@apollo/react-hooks';
import { graphqlSchema } from '../../services/graphql.schema';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { parseStampErrorFromGraphQL } from '../../utils/error.graphql';
import { conceptoToTotales } from '../../utils/calculate.conceptos';
import { useParams } from 'react-router-dom';
import PreviewMainBill from './preview/Preview.mainBill';
import PreviewSecondaryBills from './preview/Preview.secondaryBills';
import PreviewActions from './components/Preview.actions';

interface PreviewProps extends BaseReact {
    currentUser: ProfileQL;
}

function PreviewFactura({ currentUser, location, history }: PreviewProps) {
    const { argumentid, eventid } = useParams<{ argumentid?: string; eventid?: string }>();
    console.log('[FP] Location: ', location);
    const [toBillArgument, setToBillArgument] = useState<ToBillArguments<ToBillEvent>>(null);
    const [toBillEvents, setToBillEvents] = useState<ToBillEvent[]>(null);
    const [toPaymentEvents, setToPaymentEvents] = useState<ToBillEvent[]>(null);

    // UI Elements
    const [paymentError, setPaymentError] = useState('');
    const [stamping, setStamping] = useState(false);
    // ============
    useEffect(() => {
        // Extract state and use for current operations
        const { billArgument, billEvents, paymentEvents } = location.state as { billArgument: ToBillArguments<ToBillEvent>; billEvents: ToBillEvent[]; paymentEvents: ToBillEvent[] };
        setToBillArgument(billArgument);
        setToBillEvents(billEvents);
        setToPaymentEvents(paymentEvents);
    }, [location]);

    /** Stamping mutations share the same outcome, only the error origin changes */
    const stampingHandlers = (logLabel: string) => ({
        onCompleted: (result: { [key: string]: ToBillArguments<string> }) => {
            const billArgument = Object.values(result)[0];
            console.log(`${logLabel}: `, billArgument);
            sharedToasterSubject.next({ type: 'confirm', message: 'Factura emitida correctamente' });
            history.push({
                pathname: '/',
                state: { billArgument }
            })
        },
        onError: (e: any) => {
            console.error(`Error on ${logLabel}: `, e.graphQLErrors[0]);
            if (e.graphQLErrors[0]?.message.includes('Saldo insuficiente')) {
                sharedToasterSubject.next({ type: 'error', message: e.graphQLErrors[0].message });
            } else {
                sharedToasterSubject.next({ type: 'error', message: parseStampErrorFromGraphQL(e.graphQLErrors[0]), clearTs: 5200, clickToClose: true });
            }
            setStamping(false);
        }
    })

    const [setArgumentForStamping] = useMutation(graphqlSchema.FISCALPOP.CFDI.stampFromBillArgument, stampingHandlers('STAMP FROM BILL ARGUMENT'))
    const [editBillArgument] = useMutation(graphqlSchema.FISCALPOP.CFDI.restampBillArgument, stampingHandlers('EDIT FROM BILL ARGUMENT'))
    const [editBillEvent] = useMutation(graphqlSchema.FISCALPOP.CFDI.restampBillEvent, stampingHandlers('EDIT FROM BILL EVENT'))

    const onChangeReceptor = (property: 'nombre' | 'email' | 'zip' | 'regimen') => (value: string) => {
        const _toBillArgument = Object.assign({}, toBillArgument);
        _toBillArgument.MasterCFDI.receptor[property] = value;
        setToBillArgument(_toBillArgument);
    }

    const onChangePayment = (eventIndex: number) => (value: number) => {
        if (value < 0.01) {
            value = 0.01
        }
        const _toPaymentEvents: ToBillEvent[] = JSON.parse(JSON.stringify(toPaymentEvents));
        _toPaymentEvents[eventIndex].paymentCFDI.pagos[0].monto = value;
        // Check that payments equal 0 at the end
        const masterTotales = conceptoToTotales(toBillArgument.MasterCFDI.conceptos).total;
        const remainder = _toPaymentEvents.reduce((p, c) => {
            p -= (c.paymentCFDI.pagos[0].monto || 0);
            return p;
        }, masterTotales)
        if (Math.round(Math.abs(remainder)) > 0) {
            setPaymentError(`Total pagado es diferente al total por un monto de $ ${numberToCurrencyString(remainder)}`)
        } else {
            setPaymentError('')
        }
        setToPaymentEvents(_toPaymentEvents);
    }

    const onChangePaymentDate = (eventIndex: number) => (date: Date) => {
        console.log('Date: ', date);
        const _toPaymentEvents: ToBillEvent[] = JSON.parse(JSON.stringify(toPaymentEvents));
        _toPaymentEvents[eventIndex].when = date.getTime();
        setToPaymentEvents(_toPaymentEvents);
    }

    const submitArgument = () => {
        setStamping(true);
        // Make sure that no monto 0 payment event is passed
        const _toPaymentEvents = toPaymentEvents.filter(tPE => !!tPE.paymentCFDI.pagos[0].monto);
        const variables = {
            argument: toBillArgument,
            billEvents: toBillEvents,
            paymentEvents: _toPaymentEvents
        };
        if (eventid && argumentid) {
            // Edit single billEvent
            editBillEvent({ variables: Object.assign({ billEventToEditId: eventid }, variables) })
        }
        else if (argumentid) {
            // Edit and replace whole bill argument
            editBillArgument({ variables: Object.assign({ billArgumentToEditId: argumentid }, variables) })
        } else {
            // Create new Bill argument
            setArgumentForStamping({ variables })
        }
    }

    const returnToModify = () => {
        const pathName = eventid ? `/facturacion/facturar/${argumentid}/${eventid}` : (!!argumentid ? `/facturacion/facturar/${argumentid}` : `/facturacion/facturar`)
        history.push({
            pathname: pathName,
            state: { billArgument: toBillArgument, billEvents: toBillEvents }
        })
    }

    return (
        <div id="FacturaPreview">
            <SectionTitleBar currentUser={currentUser} title={`Previsualizar Factura${argumentid ? ' a editar' : ''}`} />
            <div className="previewContent">
                <PreviewMainBill
                    currentUser={currentUser}
                    toBillArgument={toBillArgument}
                    toPaymentEvents={toPaymentEvents}
                    onChangeName={onChangeReceptor('nombre')}
                    onChangeEmail={onChangeReceptor('email')}
                    onChangeZip={onChangeReceptor('zip')}
                    onChangeRegimen={onChangeReceptor('regimen')}
                    onChangePayment={onChangePayment}
                    onChangePaymentDate={onChangePaymentDate}
                />
                <PreviewSecondaryBills toBillEvents={toBillEvents} />
                <PreviewActions
                    error={paymentError || null}
                    stamping={stamping}
                    onReturn={returnToModify}
                    onSubmit={submitArgument}
                />
            </div>
        </div>
    )
}

export default PreviewFactura;
